<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { CustomCategory } from '../../../types/customCategory';
import { getCategoryRoute, getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { Schemas } from '@shopware/api-client/api-types';
import SharedSupplierContactPerson from '../../Shared/Supplier/SharedSupplierContactPerson.vue';
import SharedSupplierContactForm from '../../Shared/Supplier/SharedSupplierContactForm.vue';

const { getSupplier, getSocialMediaLinks, getProductCategories } = useSupplier();
const { getMedia, getSpecificMedia } = useGetMedia();
const modalController = useModal();

const prefixUrl = (url: string) => {
    if (url.includes('http')) {
        return url;
    }

    return `https://${url}`;
};

const category = inject('category') as Ref<CustomCategory> | undefined;
const supplier = await getSupplier(category);
const socialMediaLinks = getSocialMediaLinks(supplier);
const { data: medias } = await getMedia([
    supplier.translated?.customFields?.contactPersonMedia,
    supplier.translated?.customFields?.backgroundImage,
]);
const contactPersonImage = getSpecificMedia(supplier.translated?.customFields?.contactPersonMedia, medias.value);
const backgroundImage = getSpecificMedia(supplier.translated?.customFields?.backgroundImage, medias.value);
const productCategory: Schemas['Category'] | null = await getProductCategories(category.value);
</script>

<template>
    <template v-if="supplier">
        
<FormKitLazyProvider config-file="true">
<div class="md:grid md:grid-cols-2 md:gap-8">
            <div
                class="mt-4 md:m-0"
                :class="{
                    '-mx-6 mb-14': backgroundImage,
                    'mb-2': !backgroundImage,
                }"
            >
                <div class="relative">
                    <img
                        v-if="backgroundImage"
                        :src="backgroundImage.url"
                        :alt="backgroundImage.translated.alt"
                        class="aspect-[2/1] w-full md:aspect-[4/2.5]"
                    >
                    <div
                        v-if="supplier.media"
                        :class="{
                            'absolute -bottom-8 ml-6 h-16 bg-neutral-100 p-2': backgroundImage,
                        }"
                    >
                        <img
                            :src="supplier.media?.url"
                            :alt="supplier.media?.alt"
                            class="h-full"
                        >
                    </div>
                </div>

                <SharedSupplierContactPerson
                    class="hidden md:mt-12 md:flex"
                    :contact-person-image="contactPersonImage"
                    :supplier="supplier"
                />
            </div>

            <div>
                <div class="space-y-4">
                    <h1 class="text-3xl font-extrabold leading-8 md:text-4xl md:leading-10">
                        {{ getTranslatedProperty(supplier, 'name') }}
                    </h1>

                    <!-- premium badge -->
                    <div
                        v-if="supplier.translated.customFields.isPartner"
                        class="flex w-max items-center gap-1.5 bg-neutral-300 px-2 py-1 text-sm leading-5"
                    >
                        <FormKitIcon
                            icon="premium-partner"
                            class="block h-6 w-6"
                        />
                        {{ $t('supplier.premiumPartner') }}
                    </div>

                    <!-- v-html is necessary because we need to render html -->
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                        v-if="getTranslatedProperty(supplier, 'description')"
                        class="text-sm leading-5 md:text-base md:leading-6"
                        v-html="getTranslatedProperty(supplier, 'description')"
                    />
                </div>

                <!-- contact information -->
                <div
                    v-if="supplier.translated.customFields.address || supplier.translated.customFields.email"
                    class="mt-6 space-y-2 md:mt-8"
                >
                    <h3 class="font-extrabold">
                        {{ $t('supplier.contact') }}
                    </h3>

                    <!-- v-html is necessary because we need to render html -->
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                        v-if="supplier.translated.customFields.address"
                        v-html="supplier.translated.customFields.address"
                    />
                    <a
                        :href="prefixUrl(getTranslatedProperty(supplier, 'link'))"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-bold underline"
                    >
                        {{ getTranslatedProperty(supplier, 'link') }}
                    </a>
                </div>

                <div class="md:mt-8 md:flex">
                    <FormKit
                        id="contact-supplier-modal"
                        type="button"
                        :label="$t('supplier.contactFields.label')"
                        :classes="{
                            outer: 'w-max mt-6 md:mt-0 md:mr-4',
                            input: 'font-extrabold text-sm text-white !bg-neutral-800 py-0',
                        }"
                        @click="modalController.open()"
                    />

                    <LazySharedModal
                        :controller="modalController"
                        :with-close-button="true"
                    >
                        <template #title>{{ $t('supplier.contactFields.yourRequest') }}</template>
                        <template #content>
                            <SharedSupplierContactForm @close="modalController.close()" />
                        </template>
                    </LazySharedModal>

                    <!-- social media links -->
                    <div
                        v-if="socialMediaLinks?.length"
                        class="flex items-center gap-4"
                    >
                        <a
                            v-for="socialMediaLink in socialMediaLinks"
                            :key="socialMediaLink.name"
                            :href="socialMediaLink.url"
                            target="_blank"
                            class="flex h-11 w-11 items-center justify-center rounded-full border"
                        >
                            <FormKitIcon
                                class="block h-3.75 w-3.75 cursor-pointer"
                                :icon="socialMediaLink.name.toLocaleLowerCase()"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- contact person -->
        <SharedSupplierContactPerson
            class="flex md:hidden"
            :contact-person-image="contactPersonImage"
            :supplier="supplier"
        />

        <!-- products -->
        <div
            v-if="productCategory?.children?.length"
            class="mt-8 md:mt-32"
        >
            <h3 class="text-xl font-extrabold md:text-3xl">
                {{ $t('supplier.shopOurProducts') }}
            </h3>

            <div class="my-4 grid grid-cols-2 gap-x-2 gap-y-4 md:my-6 md:grid-cols-4">
                <template
                    v-for="childCategory in productCategory.children"
                    :key="childCategory.id"
                >
                    <LocaleLink
                        :to="getCategoryRoute(childCategory)"
                        class="bg-neutral-400 p-4 text-center font-extrabold"
                        :class="{
                            'pb-4': !childCategory.media,
                            'pb-0': childCategory.media,
                        }"
                    >
                        {{ getTranslatedProperty(childCategory, 'name') }}
                        <img
                            v-if="childCategory.media"
                            :src="childCategory.media.url"
                            :alt="childCategory.media.alt"
                            class="mx-auto mt-2 max-h-24 max-w-28 md:max-h-36 md:max-w-40"
                        >
                    </LocaleLink>
                </template>

                <div class="col-span-2 flex justify-center md:col-span-4 md:mt-6">
                    <LocaleLink :to="getCategoryRoute(productCategory)">
                        <FormKit
                            type="button"
                            :classes="{
                                outer: 'flex justify-center',
                                input: 'w-max',
                                wrapper: 'w-max',
                            }"
                        >
                            {{ $t('supplier.seeAllProducts') }}
                        </FormKit>
                    </LocaleLink>
                </div>
            </div>
        </div>
</FormKitLazyProvider>

    </template>
</template>
